// Écran Statistiques (phase 5) : clics sur les bannières (7 / 30 jours et variation, par campagne,
// par charte, top 10 des personnes — calculés par la fonction SQL click_stats(), pas ici) et
// invocations des routes publiques Cloudflare (/a/, /b/, /go/, toutes les requêtes reçues, servies
// du cache ou non — un hit de cache compte comme une requête dans le quota ; alerte à 50 000 / jour).
// État vide soigné : pendant des semaines cet écran affichera trois clics ou zéro ; chaque section
// dit en une phrase pourquoi il n'y a rien à montrer.
import type { ClickStats, EdgeHit } from '../api.ts';
import { fmtDate, h, mount } from '../dom.ts';
import type { Store } from '../state.ts';

/** Quota gratuit Cloudflare : 100 000 requêtes de Functions par jour, partagées avec les Workers ; alerte à la moitié. */
export const EDGE_ALERT_PER_DAY = 50_000;
export const EDGE_QUOTA_PER_DAY = 100_000;

const n = (v: number) => v.toLocaleString('fr-FR');
const plural = (v: number, s: string, p = `${s}s`) => `${n(v)} ${v > 1 ? p : s}`;

/** « +40 % », « −20 % », ou la raison pour laquelle on ne peut pas comparer. */
function variation(cur: number, prev: number): { text: string; cls: string } {
  if (cur === 0 && prev === 0) return { text: 'aucun clic sur les deux périodes', cls: 'muted' };
  if (prev === 0) return { text: `aucun clic sur la période précédente, ${plural(cur, 'clic')} sur celle-ci`, cls: 'ok' };
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { text: `${pct >= 0 ? '+' : '−'}${n(Math.abs(pct))} % par rapport aux ${prev === 1 ? '1 clic' : `${n(prev)} clics`} de la période précédente`, cls: pct >= 0 ? 'ok' : 'warn' };
}

const kpi = (label: string, value: number, v: { text: string; cls: string }) =>
  h('div', { class: 'kpi' }, h('div', { class: 'kpi-label' }, label), h('div', { class: 'kpi-value' }, n(value)), h('div', { class: `kpi-var ${v.cls}` }, v.text));

const table = (heads: string[], rows: (string | number | HTMLElement)[][]) =>
  h('div', { class: 'table-wrap' }, h('table', null, h('thead', null, h('tr', null, heads.map((t) => h('th', null, t)))), h('tbody', null, rows.map((r) => h('tr', null, r.map((c) => h('td', null, typeof c === 'number' ? n(c) : c)))))));

const empty = (why: string) => h('p', { class: 'empty' }, why);

export function statsView(store: Store): HTMLElement {
  const root = h('div', { class: 'stack stats' });
  const clicksBox = h('section', { class: 'panel' }, h('h3', null, 'Clics sur les bannières'), h('p', { class: 'muted' }, 'Chargement…'));
  const edgeBox = h('section', { class: 'panel' }, h('h3', null, 'Invocations des routes publiques (Cloudflare)'), h('p', { class: 'muted' }, 'Chargement…'));

  const drawClicks = async () => {
    let s: ClickStats;
    try {
      s = await store.api.clickStats();
    } catch (e) {
      return mount(clicksBox, h('h3', null, 'Clics sur les bannières'), h('p', { class: 'warn' }, `Statistiques indisponibles : ${e instanceof Error ? e.message : String(e)}`));
    }
    const noCampaignYet = !store.campaigns.some((c) => c.active);
    const onlyFallback = s.by_campaign.length > 0 && s.by_campaign.every((c) => !c.campaign_id);
    // La phrase qui dit ce qu'il y a (ou pas) à lire, avant les chiffres.
    const situation = s.total === 0
      ? 'Aucun clic enregistré pour l’instant. Un clic est compté quand quelqu’un clique une bannière dans une signature ; il n’y a rien à mesurer tant qu’aucune bannière n’est diffusée.'
      : `${plural(s.total, 'clic')} enregistré${s.total > 1 ? 's' : ''} depuis le ${fmtDate(s.first_at!)}${onlyFallback ? ', tous hors campagne (clics de test sur la redirection de repli, sans bannière)' : ''}.${noCampaignYet ? ' Aucune campagne n’est active : les répartitions par campagne et par charte se rempliront avec la première bannière en ligne.' : ''}`;
    mount(
      clicksBox,
      h('h3', null, 'Clics sur les bannières'),
      h('p', { class: 'situation' }, situation),
      h('div', { class: 'kpis' }, kpi('7 derniers jours', s.d7, variation(s.d7, s.d7_prev)), kpi('30 derniers jours', s.d30, variation(s.d30, s.d30_prev))),
      h('h4', null, 'Par jour, 30 derniers jours'),
      s.daily.length
        ? h('div', { class: 'bars' }, s.daily.map((d) => h('div', { class: 'bar', title: `${d.day} : ${plural(d.n, 'clic')}`, style: `height:${Math.max(4, Math.round((100 * d.n) / Math.max(1, ...s.daily.map((x) => x.n))))}%` })))
        : empty('Aucun clic sur les 30 derniers jours : rien à tracer.'),
      h('h4', null, 'Par campagne, 30 derniers jours'),
      s.by_campaign.length
        ? table(['Campagne', 'Clics'], s.by_campaign.map((c) => [c.name ?? 'Sans campagne (redirection de repli)', c.n]))
        : empty(noCampaignYet ? 'Aucune campagne n’a encore diffusé de bannière : rien à répartir.' : 'Aucun clic sur une campagne ces 30 derniers jours.'),
      h('h4', null, 'Par charte, 30 derniers jours'),
      s.by_brand.length ? table(['Charte', 'Clics'], s.by_brand.map((b) => [b.name ?? b.brand_id ?? '—', b.n])) : empty('Aucun clic ces 30 derniers jours : rien à répartir par charte.'),
      h('h4', null, 'Top 10 des personnes, 30 derniers jours'),
      s.top_people.length
        ? table(['Personne', 'Adresse', 'Clics'], s.top_people.map((p) => [p.name ?? '—', p.email ?? '—', p.n]))
        : empty('Personne n’a encore reçu de clic sur sa signature ces 30 derniers jours.'),
      h('p', { class: 'muted small' }, 'Les antivirus et filtres anti-hameçonnage des destinataires (Outlook Safe Links…) « cliquent » parfois les liens à la réception : les clics sont une tendance, pas une mesure exacte.'),
    );
  };

  const drawEdge = async () => {
    const hits = await store.api.listEdgeHits(14).catch(() => [] as EdgeHit[]);
    const days = [...new Set(hits.map((x) => x.day))].sort().reverse();
    const total = (day: string, route?: EdgeHit['route']) => hits.filter((x) => x.day === day && (!route || x.route === route)).reduce((acc, x) => acc + x.n, 0);
    const cachedShare = (day: string) => {
      const b = hits.filter((x) => x.day === day && x.route === 'b');
      const all = b.reduce((acc, x) => acc + x.n, 0);
      return all ? Math.round((100 * b.filter((x) => x.cached).reduce((acc, x) => acc + x.n, 0)) / all) : null;
    };
    const today = new Date().toISOString().slice(0, 10);
    const todayTotal = total(today);
    const worst = Math.max(0, ...days.map((d) => total(d)));
    const situation = days.length
      ? `${plural(todayTotal, 'requête')} aujourd’hui, ${n(worst)} au plus fort des ${days.length} derniers jours, pour une alerte à ${n(EDGE_ALERT_PER_DAY)} et un quota de ${n(EDGE_QUOTA_PER_DAY)} par jour. Les chiffres resteront très bas tant que les signatures Apple Mail / Outlook ne sont pas installées : c’est /b/ (bannière dynamique, une requête par ouverture de mail) qui portera le volume.`
      : 'Aucune requête comptée : le compteur démarre avec les premières signatures installées qui appellent nos routes.';
    mount(
      edgeBox,
      h('h3', null, 'Invocations des routes publiques (Cloudflare)'),
      h('p', { class: 'muted' }, 'Toutes les requêtes reçues par les Pages Functions /a/ (logos, icônes), /b/ (bannières dynamiques) et /go/ (clics), par jour UTC. Une réponse servie du cache compte comme une requête, exactement comme dans le quota Cloudflare : la colonne « servi du cache » ne retire rien au total, elle dit seulement combien d’appels ont évité Supabase.'),
      h('p', { class: 'situation' }, situation),
      worst >= EDGE_ALERT_PER_DAY
        ? h('p', { class: 'warnings' }, `Alerte : ${n(worst)} requêtes sur une journée, au-delà du seuil de ${n(EDGE_ALERT_PER_DAY)} (moitié du quota gratuit). Prévoir le plan Workers Paid (5 $/mois) ou réduire les appels.`)
        : null,
      days.length
        ? table(
          ['Jour', '/a/', '/b/', 'dont servi du cache', '/go/', 'Total', '% du seuil'],
          days.map((d) => [d, total(d, 'a'), total(d, 'b'), cachedShare(d) === null ? '—' : `${cachedShare(d)} %`, total(d, 'go'), h('strong', null, n(total(d))), `${Math.round((100 * total(d)) / EDGE_ALERT_PER_DAY)} %`]),
        )
        : null,
    );
  };

  mount(root, clicksBox, edgeBox);
  drawClicks();
  drawEdge();
  return root;
}
